import React, {useEffect, useState} from 'react';
import {isEmpty} from 'lodash';
import {FormattedMessage, useIntl} from 'react-intl';
import {Cell, Column} from 'react-table';
import {api} from '../../api';
import MyAreaTable from './Table/MyAreaTable'
import PageLoadingIndicator from '../Layout/PageLoadingIndicator';
import PaperSubmissionSessionModal from './PaperSubmissionSessionModal';
import ActionsCell from './Table/ActionsCell';
import {LocalizedText, PCO_PLUS_STATUS_MAP, PCOPlusSessionStatus, Person} from '../../types/types';
import DateColumn from "./Table/Columns/DateColumn";
import RadioMonthFilter, {DATE_FILTER_ALL_DATES} from "./Table/GlobalFilters/RadioMonthFilter";
import moment from "moment";

export const URL_SLUG = 'sessions';

export type SessionPersonReference = {
    id: string;
    keycloakId: string | null;
    salutation?: string;
    firstName?: string;
    lastName?: string;
    role: LocalizedText;
}

export type SessionListingDatum = {
    id: string;
    session: {
        title: string;
        subTitle?: string;
        teasertext?: string;
        location: string;
        dateFrom: string;
        dateUntil: string;
        topic: LocalizedText;
        themes: LocalizedText[];
        agendaType: string;
        eventSeries?: string[];
        personReferences: SessionPersonReference[];
    };
    paperSubmission: {
        id: string;
        callForPapersUrlSlug: string;
        title: string;
        subTitle: string;
        abstract: string;
        submittedBy: {
            id: string;
            keycloakId: string;
        };
        planningStatus: PCOPlusSessionStatus;
    };
    event: {
        name: string;
    };
}

/**
 * Tabellarisches Listing von Programmpunkten des aktuell eingeloggten Users
 *
 * @constructor
 */
const PaperSubmissionSessionListing: React.FC = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<SessionListingDatum[]>([]);
    const [filteredData, setFilteredData] = useState<SessionListingDatum[]>([]);
    const [person, setPerson] = useState<Person | null>(null);
    const [currentSession, setCurrentSession] = useState<SessionListingDatum | null>(null);
    const intl = useIntl();

    useEffect(() => {
        const loadSessionsForUser = async () => {

            await api.fetchSelf().then(res => {
                setPerson({...res});
            });

            await api.fetchSessionsForUser().then(res => {
                setIsLoading(false);
                if (res?.results) {
                    setData(res.results);
                }
            });
        };

        loadSessionsForUser();
    }, []);

    // siehe https://github.com/tannerlinsley/react-table/discussions/2664#discussioncomment-335443
    const columns = React.useMemo<Column<SessionListingDatum>[]>(
        () => [
            {
                id: 'eventName',
                Header: <FormattedMessage id="myarea.listing.column.header.event"/>,
                accessor: (row: SessionListingDatum) => row.event.name,
                Cell: (cell: Cell) => <span>{cell.row.values.eventName}</span>
            },
            {
                id: 'sessionTitle',
                Header: <FormattedMessage id="myarea.listing.column.header.session"/>,
                accessor: (row: SessionListingDatum) => row.session.title,
                Cell: (cell: Cell) => <span>{cell.row.values.sessionTitle}</span>
            },
            {
                id: 'sessionDateFrom',
                Header: <FormattedMessage id="myarea.listing.column.header.sessionFrom"/>,
                accessor: (row: SessionListingDatum) => row.session.dateFrom,
                Cell: (cell: Cell) => DateColumn(cell, 'sessionDateFrom')
            },
            {
                id: 'sessionDateUntil',
                Header: <FormattedMessage id="myarea.listing.column.header.sessionUntil"/>,
                accessor: (row: SessionListingDatum) => row.session.dateUntil,
                Cell: (cell: Cell) => DateColumn(cell, 'sessionDateUntil')
            },
            {
                id: 'location',
                Header: <FormattedMessage id="myarea.listing.column.header.room"/>,
                accessor: (row: SessionListingDatum) => row.session.location,
                Cell: (cell: Cell) => <span>{cell.row.values.location}</span>
            },
            {
                id: 'planningStatus',
                Header: <FormattedMessage id="myarea.listing.column.header.planningStatus"/>,
                accessor: (row: SessionListingDatum) => row.paperSubmission.planningStatus,
                Cell: (cell: Cell) => {
                    const planningStatus = PCO_PLUS_STATUS_MAP[cell.row.values.planningStatus as PCOPlusSessionStatus];
                    return <span>{planningStatus ? intl.formatMessage({id: planningStatus}) : ''}</span>;
                }
            },
            {
                id: 'actions',
                disableSortBy: true,
                Header: <FormattedMessage id="myarea.listing.column.header.actions"/>,
                accessor: (row: SessionListingDatum) => row,
                Cell: (cell: Cell) => <ActionsCell personId={person?.id}
                                                   session={cell.row.values.actions as SessionListingDatum}
                                                   setCurrentSession={setCurrentSession}
                />
            }
        ],
        [person, intl]
    );

    /**
     * Filtert das Listing anhand eines übergebenen filterValue und gibt nur die Einträge zurück, die jünger als
     * filterValue Monate sind.
     *
     * @param filterValue - nummerische Angabe der unteren Schranke an Monaten, die die relevante Spalte in der
     *                      Vergangenheit liegen darf
     */
    const filterByDate = (filterValue: number | undefined): void => {
        if (filterValue !== DATE_FILTER_ALL_DATES) {
            // moment-date bilden mit dem ausgewählten Monatsfilter
            const filterDate = moment().subtract(filterValue, 'months');
            setFilteredData(data.filter((datum: any) => {
                const date = moment(datum['session']['dateFrom']);
                if (date && filterDate) {
                    return date.isAfter(filterDate);
                }

                // sofern wir kein Datum haben, geben wir die Zeile einfach aus weil lieber sehen als nicht ¯\_(ツ)_/¯
                return true;
            }))
        } else {
            setFilteredData(data);
        }
    };

    return (
        <div className="submission-listing-container">
            <h2><FormattedMessage id="myarea.sessions.listing.title"/></h2>
            <p><FormattedMessage id="myarea.sessions.listing.subtitle"/></p>

            {isLoading && <PageLoadingIndicator/>}

            {!isEmpty(data) &&
            <>
                <RadioMonthFilter filterByDate={filterByDate}/>

                <MyAreaTable<SessionListingDatum>
                    columns={columns}
                    data={filteredData}
                />
                <PaperSubmissionSessionModal
                    session={currentSession}
                    onHide={() => setCurrentSession(null)}
                />
            </>
            }
        </div>
    );
};

export default PaperSubmissionSessionListing;
